import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface Team {
  id: string;
  name: string;
  slug: string;
}

interface HeatmapCompetency {
  id: string;
  name: string;
  dimension: 'technical' | 'behavioral';
  sort_order: number;
}

interface HeatmapCell {
  competency_id: string;
  xp: number;
  level: number;
}

interface HeatmapUser {
  id: string;
  display_name: string;
  handle: string;
  role_in_team: string;
  cells: HeatmapCell[];
}

interface HeatmapResponse {
  team_id: string;
  competencies: HeatmapCompetency[];
  users: HeatmapUser[];
}

// Team competency heatmap. Rows = team members, columns = the 12
// Forbury competencies, cells = game level (colour-coded by intensity).
// A lead/admin can see the team's relative strengths and gaps at a
// glance. Cells show the numeric game level; colour is supplementary
// (the table is semantic HTML for screen readers).
//
// IMPORTANT: levels are the gamified RPG progression (0-12+), NOT the
// Forbury framework's career-level names. See ADR-0008.

// Map a game level to a background opacity of the accent colour.
// Capped at level 5 (Master tier) — anything beyond is full intensity.
function levelOpacity(level: number): number {
  if (level <= 0) return 0;
  if (level >= 5) return 1;
  return level * 0.2; // 1→0.2, 2→0.4, 3→0.6, 4→0.8
}

const RPG_TIER_NAMES = ['—', 'Novice', 'Apprentice', 'Adept', 'Expert', 'Master'];

function tierName(level: number): string {
  if (level <= 0) return '—';
  if (level >= 5) return 'Master+';
  return RPG_TIER_NAMES[level] ?? `L${level}`;
}

export function TeamHeatmapPage() {
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api<{ teams: Team[] }>('/api/teams') });
  const [teamId, setTeamId] = useState<string>('');

  const selectedTeam = teamId || teams.data?.teams?.[0]?.id || '';
  const heatmap = useQuery({
    queryKey: ['team-heatmap', selectedTeam],
    queryFn: () => api<HeatmapResponse>(`/api/admin/teams/${selectedTeam}/heatmap`),
    enabled: !!selectedTeam,
  });

  const competencies = heatmap.data?.competencies ?? [];
  const users = heatmap.data?.users ?? [];
  const technical = competencies.filter((c) => c.dimension === 'technical');
  const behavioral = competencies.filter((c) => c.dimension === 'behavioral');

  // Team-average level per competency (for the summary row).
  const avgLevel = (compId: string): number => {
    const cells = users.map((u) => u.cells.find((c) => c.competency_id === compId)).filter(Boolean) as HeatmapCell[];
    if (cells.length === 0) return 0;
    return cells.reduce((sum, c) => sum + c.level, 0) / cells.length;
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Competency heatmap</h1>
        <p className="text-ink-muted">
          Each cell is a team member's game level in a competency. Brighter = more
          progression. Use it to spot the team's strengths and gaps — not to judge
          individuals (levels reflect observed activity, not readiness for anything).
        </p>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="label" htmlFor="heatmap-team">Team</label>
          <select
            id="heatmap-team"
            className="input"
            value={selectedTeam}
            onChange={(e) => setTeamId(e.target.value)}
          >
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <Legend />
      </div>

      {heatmap.isLoading ? (
        <p className="text-ink-muted">Loading heatmap…</p>
      ) : heatmap.error ? (
        <p className="text-accent-danger">Failed to load heatmap.</p>
      ) : users.length === 0 ? (
        <div className="card text-center text-ink-muted">
          No members in this team yet. Add members on the Teams page.
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="py-2 pr-3">Member</th>
                {technical.map((c) => (
                  <th key={c.id} scope="col" className="px-1 py-2 text-center text-xs" title={c.name}>
                    <span className="inline-block max-w-[5rem] truncate align-bottom">{c.name}</span>
                  </th>
                ))}
                {behavioral.map((c) => (
                  <th key={c.id} scope="col" className="px-1 py-2 text-center text-xs" title={c.name}>
                    <span className="inline-block max-w-[5rem] truncate align-bottom">{c.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-white/5">
                  <th scope="row" className="py-1.5 pr-3 text-left text-xs font-medium">
                    {u.display_name}
                    <span className="block text-ink-dim">@{u.handle}</span>
                  </th>
                  {competencies.map((c) => {
                    const cell = u.cells.find((x) => x.competency_id === c.id);
                    const level = cell?.level ?? 0;
                    const xp = cell?.xp ?? 0;
                    return (
                      <td key={c.id} className="px-1 py-1.5 text-center">
                        <div
                          className="grid h-8 w-12 place-items-center rounded-md tabular-nums text-xs font-semibold mx-auto"
                          style={{
                            backgroundColor: level > 0
                              ? `var(--accent-opacity-${Math.min(level, 5) * 20}, rgba(99,102,241,${levelOpacity(level)}))`
                              : 'var(--bg-elevated, #1e1e2e)',
                            color: level >= 3 ? 'var(--bg, #0a0a0f)' : 'var(--ink-muted, #999)',
                          }}
                          title={`${c.name}: ${xp} XP, Level ${level} (${tierName(level)})`}
                        >
                          {level > 0 ? level : '·'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Summary row: team-average level per competency */}
              <tr className="border-t-2 border-white/10">
                <th scope="row" className="py-1.5 pr-3 text-xs font-semibold uppercase tracking-wide text-ink-dim">
                  Team avg
                </th>
                {competencies.map((c) => {
                  const avg = avgLevel(c.id);
                  return (
                    <td key={c.id} className="px-1 py-1.5 text-center text-xs tabular-nums text-ink-muted">
                      {avg > 0 ? avg.toFixed(1) : '·'}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Legend() {
  const levels = [0, 1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-2 text-xs text-ink-muted">
      <span className="label">Level</span>
      {levels.map((l) => (
        <span key={l} className="flex items-center gap-1">
          <span
            className="inline-block h-4 w-4 rounded"
            style={{
              backgroundColor: l > 0
                ? `rgba(99,102,241,${levelOpacity(l)})`
                : 'var(--bg-elevated, #1e1e2e)',
            }}
          />
          {l === 5 ? '5+' : l}
        </span>
      ))}
    </div>
  );
}